<template>
  <a :href="urlSlug" class="related-summary-item">
    <div class="related-summary-item__image-wrapper">
      <img
        v-if="hasThumbnail"
        class="related-summary-item__image"
        :src="item.thumbnail"
      />
      <BookFallbackCover
        v-else
        class="related-summary-item__fallback-image"
        :title="item.title"
        :subtitle="item.author"
      />
    </div>
    <div>
      <h5 class="related-summary-item__title">{{ item.title }}</h5>
      <h6 class="related-summary-item__author">{{ item.author }}</h6>
      <div class="related-summary-item__blurb" v-html="item.blurb"></div>
    </div>
  </a>
</template>

<script setup lang="ts">
import { RelatedSummaryItem } from '@/models/interfaces'
import { computed } from 'vue'
import BookFallbackCover from '@/app/components/book/card/BookFallbackCover.vue'

const props = defineProps<{
  item: RelatedSummaryItem
}>()

// We are not using router-link for the url here, because we're rendering the
// AdditionalInformationBlock as HTML and hence it doesn't exist
// github.com/shortformhq/main/blob/bb5c30ccf1b71510e29289e8d097e9e38701cc09/frontend/src/app/components/doc/reading/Chapter.vue#L64-L85
const urlSlug = computed(() => {
  // We return the full url from backend, stripping away the hostname here
  return props.item.url.replace('https://www.shortform.com', '')
})

// AI books don't have cover images: instead, it's returned as 'https:None'.
// This is a hotfix solution: AI books shouldn't even appear in the
// related summaries section.
const hasThumbnail =
  props.item.thumbnail && props.item.thumbnail !== 'https:None'
</script>

<style lang="scss" scoped>
.related-summary-item {
  display: flex;
  text-decoration: none;

  background: $c-white;
  border: 1px solid $c-border-7;
  border-radius: 8px;
  margin-top: 12px;
  padding: 16px;
  gap: 12px;
  transition: 0.3s;

  @include hover {
    text-decoration: none;
    color: inherit;
    border-color: $c-black-3;
  }

  &__image {
    border-radius: 6px;
    width: 68px;
    &-wrapper {
      min-width: 67.6px;
    }
  }

  &__fallback-image {
    --cover-width: 68px;
  }

  &__title {
    font-size: 16px;
    line-height: 20px;
    letter-spacing: -0.02em;
    color: $c-black-2;
    padding-right: 8px;
    margin-bottom: 2px;
  }
  &__link {
    color: $c-blue-4;
    font-size: 12px;
    display: flex;

    &-icon {
      margin-left: 4px;
      display: flex;
      margin-top: 3px;
    }
  }
  &__author {
    font-size: 12px;
    font-weight: 500;
    line-height: 18px;
    letter-spacing: -0.02em;

    color: $c-black-3;
    padding-bottom: 8px;
    border-bottom: 1px solid $c-black-13;
    margin-right: 20px;
    margin-bottom: 0px;
  }
  &__blurb {
    margin-top: 8px;
    padding-right: 20px;

    :deep(p, li, blockquote) {
      font-size: 12px;
      line-height: 16px;
      letter-spacing: -0.02em;
      color: $c-black-3;
    }

    :deep(> *) {
      margin-bottom: 0;
    }

    // Only show the first element of the blurb
    & :deep(> *:not(:first-child)) {
      display: none;
    }
  }
}

.night-mode .related-summary-item {
  background: $c-black-9;
  border-color: $c-black-10;

  @include hover {
    border-color: $c-white;
  }

  &__title {
    color: $c-white;
  }

  &__author {
    color: $c-dark-6;
    border-color: $c-black-10;
  }

  &__blurb {
    :deep(p, li, blockquote) {
      color: $c-dark-6;
    }
  }

  &__link {
    color: $c-white;
  }
}
</style>
